import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAnalytics } from '@center-technologies/analytics';
import {
  useApiClientsOrNull,
  useSphereType,
  useArcTemplates,
  SphereTypeComposerOrganism,
  SPHERE_TYPE_SECTION_COUNT,
} from 'design-system/my-lib';

import './SphereTypeEditorPage.scss';

const COMPOSER_AGENT_ENDPOINT = process.env.REACT_APP_COMPOSER_AGENT_ENDPOINT;

/**
 * `/studio/types/:schemaId` — the Sphere Type Composer. Purely a thin route host:
 * `useSphereType` owns the fetch/lineage-walk/draft-overlay/save, `SphereTypeComposerOrganism`
 * owns rendering. No usePlatformAdmin/useOrgAdmin gating here — the route's own 404/degraded
 * states from the server's `view` check ARE the gate, and `saveField` itself resolves `'locked'`
 * server-side (`canEdit`) rather than this page deciding who may write; `is_system_schema` is
 * display-only.
 *
 * Fires `sphere_type_opened` once per resolved fetch and `sphere_type_section_viewed` at most
 * once per section as its anchor enters the viewport — the evidence that should drive Phase 2
 * (authoring) ordering, per the spec's Analytics table.
 */
const SphereTypeEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schemaId } = useParams<{ schemaId: string }>();
  const {
    status,
    definition,
    lineage,
    saveField,
    saving,
    saveDraft,
    savingDraft,
    conflict,
    saveError,
    gate,
    gateStatus,
    needsByAspect,
    aspectCatalog,
    publishing,
    publish,
    draftAspect,
  } = useSphereType(schemaId);
  // The arc templates the Arcs block names, gates and offers. Read here rather
  // than inside the composer, like `needsByAspect`: the page owns the reads.
  const { templates: arcTemplates } = useArcTemplates(status === 'ready');
  const logAnalyticsEvent = useAnalytics();
  const openedForSchemaId = useRef<string | null>(null);
  const apiClients = useApiClientsOrNull();

  // The org-bearing session token first (same source as every ApiClients call — the
  // interceptor's tokenProvider); the bare Descope session token only as the fallback the
  // interceptor itself uses. A bare session token has no organization claim and would 401 the schema-service call the agent's
  // grounding tools make when it forwards this token.
  const agentGetToken = useCallback(
    async () => {
      // The org-bearing session token from the ApiClients tokenProvider - the same source every
      // API call uses. There is no second token source: a bare identity token would carry no
      // organization claim and 401 org-scoped routes when the agent service forwards it.
      const token = await apiClients?.getAuthToken?.();
      if (!token) throw new Error('No session token available for the agent client');
      return token;
  },
    [apiClients]
  );

  useEffect(() => {
    if (status !== 'ready' || !definition || openedForSchemaId.current === schemaId) return;
    openedForSchemaId.current = schemaId ?? null;
    logAnalyticsEvent('sphere_type_opened', undefined, {
      schemaId,
      systemName: definition.system_name,
      isSystem: definition.is_system_schema,
      isAbstract: definition.is_abstract,
      sectionCount: SPHERE_TYPE_SECTION_COUNT,
    });
  }, [status, definition, schemaId, logAnalyticsEvent]);

  // Stable identity: the organism observes 12 anchors keyed on this callback, so
  // a fresh closure each render tore the IntersectionObserver down and rebuilt it
  // on every parent render.
  const handleSectionView = useCallback(
    (sectionId: string) => {
      logAnalyticsEvent('sphere_type_section_viewed', undefined, { schemaId, sectionId });
    },
    [logAnalyticsEvent, schemaId]
  );

  // `state.from` is what the gallery set on its own `navigate` call — never a query param, so a
  // bookmarked/shared editor URL never leaks the origin gallery into the address bar. `/studio/types`
  // has no gallery of its own; it is an unmatched segment under `/studio` and the shell's own
  // catch-all route redirects it to the Studio home, which is the correct fallback when the editor
  // was opened with no known origin (a direct link, a reload).
  const from = (location.state as { from?: string } | null)?.from;
  const handleBack = useCallback(() => {
    navigate(from ?? '/studio/types');
  }, [navigate, from]);

  /** The composer's sandbox sphere, opened where every other sphere opens. */
  const handleOpenSphere = useCallback(
    (sphereId: string) => {
      navigate(`/sphere/${sphereId}`);
    },
    [navigate]
  );

  const handleSaveDraft = useCallback(
    (pendingFields: number) => {
      void saveDraft(pendingFields);
    },
    [saveDraft]
  );

  const handlePublish = useCallback(async () => {
    const outcome = await publish();
    if (typeof outcome === 'object' && outcome.outcome === 'published') {
      logAnalyticsEvent('sphere_type_published', undefined, {
        schemaId,
        version: outcome.version,
        openChecksAtPublish: 0,
      });
    }
    return outcome;
  }, [publish, logAnalyticsEvent, schemaId]);

  return (
    <div className="sphere-type-editor-page" data-testid="sphere-type-editor-page">
      <SphereTypeComposerOrganism
        status={status}
        definition={definition}
        lineage={lineage}
        onSectionView={handleSectionView}
        onSaveField={saveField}
        onSaveDraft={handleSaveDraft}
        onOpenSphere={handleOpenSphere}
        savingDraft={savingDraft}
        saving={saving}
        conflict={conflict}
        saveError={saveError}
        gate={gate}
        gateStatus={gateStatus}
        needsByAspect={needsByAspect}
        aspectCatalog={aspectCatalog}
        arcTemplates={arcTemplates}
        publishing={publishing}
        onPublish={handlePublish}
        onDraftAspect={draftAspect}
        agentEndpoint={COMPOSER_AGENT_ENDPOINT}
        agentGetToken={agentGetToken}
        agentTarget={schemaId ? { kind: 'schema', id: schemaId } : undefined}
        onBack={handleBack}
      />
    </div>
  );
};

export default SphereTypeEditorPage;
